import { ApolloClient, InMemoryCache, split, HttpLink } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { setContext } from "@apollo/client/link/context";

let wsClient = null;

function getWsClient() {
  if (!wsClient) {
    wsClient = createClient({
      url: process.env.NEXT_PUBLIC_GRAPHQL_WS_URL,
      connectionParams: () => {
        if (typeof window === "undefined") return {};
        try {
          const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
          return { authToken: userInfo?.token || null };
        } catch {
          return {};
        }
      },
      lazy: true,
      retryAttempts: 10,
      shouldRetry: () => true,
      on: {
        connected: () => console.log("[WS] connected"),
        closed: (event) =>
          console.log("[WS] closed", event?.code, event?.reason || ""),
        error: (error) => console.error("[WS] error", error),
      },
    });
  }
  return wsClient;
}

const wsLink =
  typeof window !== "undefined" ? new GraphQLWsLink(getWsClient()) : null;

const httpLink = new HttpLink({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
});

const authLink = setContext((_, { headers }) => {
  if (typeof window === "undefined") return { headers };
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const token = userInfo?.token;
    return {
      headers: {
        ...headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  } catch {
    return { headers };
  }
});

const splitLink =
  typeof window !== "undefined" && wsLink
    ? split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      authLink.concat(httpLink)
    )
    : authLink.concat(httpLink);

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

// Force-reset WebSocket connection to ensure fresh auth token
export const resetWebSocket = () => {
  if (wsClient) {
    try {
      wsClient.terminate(); // Close connection
    } catch (e) {
      console.warn("Error terminating client", e);
    }
    wsClient = null; // Clear instance so getWsClient() creates a new one
  }
  // Warm up new connection immediately
  if (typeof window !== "undefined") {
    getWsClient();
  }
};

export const ApolloProviderWrapper = ({ children }) => (
  <ApolloProvider client={client}>{children}</ApolloProvider>
);
